import React from 'react';
import {Box, styled} from '@mui/material';
import {Body, Icon, designSystemToken} from '@lightricks/react-design-system';
import {Platform} from '@contentlab/api/avatars/types';
import translate from '@/utils/translate';

interface PlatformOption {
  id: Platform;
  localeLabelKey: string;
  iconName: string;
}

const PLATFORM_OPTIONS: PlatformOption[] = [
  {
    id: Platform.Facebook,
    localeLabelKey: 'Facebook',
    iconName: 'Social-Facebook',
  },
  {
    id: Platform.Instagram,
    localeLabelKey: 'Instagram',
    iconName: 'Social-Instagram',
  },
  {
    id: Platform.YouTube,
    localeLabelKey: 'YouTube',
    iconName: 'Social-YouTube',
  },
  {id: Platform.TikTok, localeLabelKey: 'TikTok', iconName: 'Social-TikTok'},
  {
    id: Platform.LinkedIn,
    localeLabelKey: 'LinkedIn',
    iconName: 'Social-LinkedIn',
  },
  {
    id: Platform.XTwitter,
    localeLabelKey: 'X (Twitter)',
    iconName: 'Social-X',
  },
  {id: Platform.Reddit, localeLabelKey: 'Reddit', iconName: 'Social-Reddit'},
  {
    id: Platform.Pinterest,
    localeLabelKey: 'Pinterest',
    iconName: 'Social-Pinterest',
  },
  {
    id: Platform.Snapchat,
    localeLabelKey: 'Snapchat',
    iconName: 'Social-Snapchat',
  },
  {id: Platform.Threads, localeLabelKey: 'Threads', iconName: 'Social-Threads'},
];

const FormSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const SelectionContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

const PlatformsGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: '16px',
}));

const shouldNotForwardProps = ['isSelected'];

const PlatformButton = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({isSelected}: {isSelected: boolean}) => ({
  'display': 'flex',
  'minHeight': '40px',
  'padding': '24px 12px',
  'flexDirection': 'column',
  'justifyContent': 'center',
  'alignItems': 'center',
  'gap': '8px',
  'flex': '1 0 0',
  'alignSelf': 'stretch',
  'borderRadius': '16px',
  'fontSize': '14px',
  'fontWeight': 500,
  'cursor': 'pointer',
  'transition': 'all 0.2s ease-in-out',
  'border': `1.5px solid ${
    isSelected
      ? designSystemToken('semantic.bg.neutral')
      : designSystemToken('semantic.bg.neutral-inverse')
  }`,
  'backgroundColor': isSelected
    ? 'transparent'
    : designSystemToken('semantic.bg.primary'),
  'color': designSystemToken('semantic.fg.primary'),
  '&:hover': {
    borderColor: designSystemToken('semantic.bg.neutral'),
    backgroundColor: designSystemToken('semantic.bg.tertiary'),
  },
}));

interface PlatformSectionProps {
  testID?: string;
  selectedPlatforms: string[];
  onPlatformSelect: (platform: string) => void;
  label?: string;
}

function PlatformSection({
  testID = 'platform-section',
  selectedPlatforms,
  onPlatformSelect,
  label = 'Platform',
}: PlatformSectionProps) {
  return (
    <FormSection data-testid={testID}>
      <SelectionContainer>
        <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
          {translate(label)}
        </Body>
        <PlatformsGrid>
          {PLATFORM_OPTIONS.map((option) => {
            const isSelected = selectedPlatforms.includes(option.id);
            return (
              <PlatformButton
                key={option.id}
                onClick={() => onPlatformSelect(option.id)}
                isSelected={isSelected}
              >
                <Icon
                  name={
                    option.iconName as React.ComponentProps<typeof Icon>['name']
                  }
                  size="medium"
                  appearance="neutral"
                />
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.neutral')}
                >
                  {translate(option.localeLabelKey)}
                </Body>
              </PlatformButton>
            );
          })}
        </PlatformsGrid>
      </SelectionContainer>
    </FormSection>
  );
}

export default PlatformSection;
